import { useMemo, useState } from "react";
import { Toaster } from "sonner";
import { Nav } from "@/components/landing/Nav";
import { Hero } from "@/components/landing/Hero";
import { TrustStrip } from "@/components/landing/TrustStrip";
import { UseCasesIndex } from "@/components/landing/UseCasesIndex";
import { CategorySection } from "@/components/landing/CategorySection";
import { LeadForm } from "@/components/landing/LeadForm";
import { Footer } from "@/components/landing/Footer";
import { useSite } from "@/context/SiteContext";
import { LeadModalProvider } from "@/context/LeadModalContext";

export default function LandingPage() {
  const { categories, industries, tags, search_prompts, loading } = useSite();
  const [query, setQuery] = useState("");
  const [activeIndustry, setActiveIndustry] = useState(""); // "" = all
  const [highlightedId, setHighlightedId] = useState(null);

  // Filter logic - tokenize the query, strip stopwords, AND-match each token across
  // (title, description, cam_id, category name/tagline/summary, tag and industry names).
  // If AND yields zero, fall back to OR so multi-word phrases like
  // "AI on Port Operations" still surface relevant features instead of an empty page.
  const filteredCategories = useMemo(() => {
    const STOPWORDS = new Set([
      "ai", "on", "the", "a", "an", "of", "for", "in", "to", "with",
      "and", "or", "by", "at", "is", "are", "be",
    ]);
    const rawQ = query.trim().toLowerCase();
    const allTokens = rawQ.split(/\s+/).filter(Boolean);
    let tokens = allTokens.filter((t) => t.length >= 2 && !STOPWORDS.has(t));
    if (tokens.length === 0 && allTokens.length > 0) tokens = [rawQ];
    if (tokens.length === 0 && !activeIndustry) return categories;

    const tagById = Object.fromEntries(tags.map((t) => [t.id, t.name.toLowerCase()]));
    const industryById = Object.fromEntries(industries.map((i) => [i.id, i.name.toLowerCase()]));

    const passesIndustry = (f) =>
      !activeIndustry || (f.industry_ids || []).includes(activeIndustry);

    const buildHaystack = (cat, f) =>
      [
        f.title,
        f.description,
        f.cam_id,
        cat.name,
        cat.tagline,
        cat.summary,
        ...(f.tag_ids || []).map((id) => tagById[id] || ""),
        ...(f.industry_ids || []).map((id) => industryById[id] || ""),
      ]
        .join(" ")
        .toLowerCase();

    const run = (predicate) =>
      categories
        .map((cat) => ({
          ...cat,
          features: (cat.features || []).filter((f) => passesIndustry(f) && predicate(buildHaystack(cat, f))),
        }))
        .filter((c) => c.features && c.features.length > 0);

    if (tokens.length === 0) {
      return run(() => true);
    }
    // Strict AND first
    let result = run((hay) => tokens.every((t) => hay.includes(t)));
    // Fallback to OR if multi-token AND returned nothing
    if (result.length === 0 && tokens.length > 1) {
      result = run((hay) => tokens.some((t) => hay.includes(t)));
    }
    return result;
  }, [categories, tags, industries, query, activeIndustry]);

  // Flat results for the hero search popup (with category name attached for the dropdown)
  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const out = [];
    for (const c of filteredCategories) {
      for (const f of c.features || []) {
        out.push({ ...f, categoryName: c.name, categorySlug: c.slug || c.id });
      }
    }
    return out;
  }, [filteredCategories, query]);

  const isFiltering = Boolean(query.trim()) || Boolean(activeIndustry);
  const totalFeatures = filteredCategories.reduce(
    (acc, c) => acc + (c.features?.length || 0),
    0
  );

  const handleResultClick = (feature) => {
    // Scroll to the matching card and briefly highlight it
    const el = document.querySelector(
      `[data-testid="feature-card-${feature.slug || feature.id}"]`
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(feature.id);
      window.setTimeout(() => setHighlightedId(null), 2000);
    }
  };

  return (
    <LeadModalProvider categories={categories}>
      <div className="min-h-screen bg-white text-neutral-900" data-testid="landing-root">
      <Nav categories={categories} />
      <main>
        <Hero
          prompts={search_prompts}
          query={query}
          onSearch={setQuery}
          results={searchResults}
          totalMatches={totalFeatures}
          onResultClick={handleResultClick}
        />
        <TrustStrip />

        {/* Industry filter bar - shown above results */}
        {industries.length > 0 && (
          <section
            id="use-cases"
            data-testid="industry-filter-bar"
            className="bg-white border-y border-neutral-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-2">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-neutral-500 mr-1">
                Industry:
              </span>
              <button
                type="button"
                data-testid="industry-chip-all"
                onClick={() => setActiveIndustry("")}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                  activeIndustry === ""
                    ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                    : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
                }`}
              >
                All
              </button>
              {industries.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  data-testid={`industry-chip-${i.slug || i.id}`}
                  onClick={() => setActiveIndustry((cur) => (cur === i.id ? "" : i.id))}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    activeIndustry === i.id
                      ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                      : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
                  }`}
                >
                  {i.name}
                </button>
              ))}
              {isFiltering && (
                <span
                  data-testid="filter-result-count"
                  className="ml-auto font-mono-ui text-[11px] uppercase tracking-[0.16em] text-neutral-600"
                >
                  {totalFeatures} match{totalFeatures === 1 ? "" : "es"}
                </span>
              )}
            </div>
          </section>
        )}

        {!isFiltering && <UseCasesIndex categories={categories} />}

        {loading && filteredCategories.length === 0 ? (
          <section className="py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                Loading use cases…
              </p>
            </div>
          </section>
        ) : filteredCategories.length === 0 ? (
          <section
            data-testid="no-results-section"
            className="py-24 border-t border-neutral-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                No matches
              </p>
              <h3 className="font-display mt-3 text-2xl sm:text-3xl font-bold text-neutral-950">
                Nothing matches &ldquo;{query}&rdquo; yet.
              </h3>
              <p className="mt-3 text-sm text-neutral-600">
                Try a different keyword, or clear filters to see all {categories.length} categories.
              </p>
              <button
                type="button"
                data-testid="clear-filters-cta"
                onClick={() => {
                  setQuery("");
                  setActiveIndustry("");
                }}
                className="mt-6 inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-neutral-800"
              >
                Clear filters
              </button>
            </div>
          </section>
        ) : (
          filteredCategories.map((c) => (
            <CategorySection
              key={c.id}
              category={c}
              tags={tags}
              industries={industries}
              highlightedId={highlightedId}
            />
          ))
        )}

        <LeadForm categories={categories} />
      </main>
      <Footer categories={categories} />
      <Toaster position="bottom-right" richColors closeButton />
      </div>
    </LeadModalProvider>
  );
}
